// IndexedDB supplies cross-tab exclusion even on self-hosted HTTP origins,
// where Web Locks is unavailable. Every achievement/stats writer uses this
// same readwrite scope. The WHOLE localStorage critical section must run
// synchronously inside onsuccess, while the transaction is active: no lease,
// timeout, await, or separate write after releasing ownership.
let factory, opened;

function database() {
  const current = globalThis.indexedDB;
  if (!current) return Promise.reject(Object.assign(new Error('IndexedDB unavailable'), { code: 'store_lock_unavailable' }));
  if (factory === current && opened) return opened;
  factory = current;
  const attempt = new Promise((resolve, reject) => {
    let failed = false;
    const request = current.open('retroplay-achievement-writers', 1);
    request.onupgradeneeded = () => request.result.createObjectStore('writers');
    request.onerror = () => reject(request.error);
    request.onblocked = () => { failed = true; reject(new Error('Achievement storage coordination is blocked')); };
    request.onsuccess = () => {
      const db = request.result;
      if (failed) { db.close(); return; }
      db.onversionchange = () => { db.close(); if (opened === attempt) opened = null; };
      db.onclose = () => { if (opened === attempt) opened = null; };
      resolve(db);
    };
  });
  opened = attempt;
  attempt.catch(() => { if (opened === attempt) opened = null; });
  return attempt;
}

/** Resolves only after exclusion ends. An abort can follow a localStorage write;
 * callers report failure, never automatically replay a non-idempotent delta. */
export function storageTransaction(run) {
  return Promise.resolve().then(database).then(db => new Promise((resolve, reject) => {
    const tx = db.transaction('writers', 'readwrite');
    let result, failure;
    tx.oncomplete = () => resolve(result);
    tx.onabort = () => reject(failure || tx.error || new Error('Achievement storage transaction aborted'));
    tx.onerror = () => {}; // onabort owns the failure
    tx.objectStore('writers').get('lock').onsuccess = () => {
      try { result = run(); }
      catch (cause) { failure = cause; tx.abort(); }
    };
  })).catch(cause => {
    if (typeof cause?.code === 'string') throw cause;
    throw Object.assign(new Error('Achievement storage coordination failed', { cause }), { code: 'store_lock_unavailable' });
  });
}
